import { addCalendarDays } from "@common/helpers/date";
import type { DensityChartWindow } from "./DensityChart.lib";
import { clampToHistory } from "./DensityChart.lib";
import type { DensityChartBucketUnit } from "./densityBuckets.lib";
import { dayPosition, toBarIndex } from "./densityBuckets.lib";
import { fitsWindow } from "./densityFit.lib";

/** Every bar is 14px with a 4px gap, whatever the chart width. */
export const BAR_PITCH_PX = 18;
/** Past today's bar at the right edge: one empty bar slot, then the page's 24px gutter, less the 2px half gap its own slot keeps. */
export const TODAY_INSET_PX = BAR_PITCH_PX + 24 - 2;
/** An arrow press moves most of a screen, keeping a few bars for context. */
const PAN_SHARE = 0.8;
/** The earlier fade's width, set on the chart as `--earlier-fade-width`. */
export const EARLIER_FADE_PX = 56;
/** Bars of the zone before the history start left in view at the earliest scroll position, 36px. */
const START_GAP_BARS = 2;

export type PanDirection = "earlier" | "later";

interface DensityTimelineParams {
    /** Where history starts. */
    startDate: string;
    /** Today, the last day with bars. */
    endDate: string;
    unit: DensityChartBucketUnit;
    width: number;
}

export interface DensityViewportParams extends DensityTimelineParams {
    /** Bars between the right edge and the end of today's bar. */
    offset: number;
}

export interface DensityViewport {
    offset: number;
    maxOffset: number;
    /** Bars across the width, fractional when it isn't a whole number of bars. */
    visibleBars: number;
    /** The first drawn bar, counted from the epoch. */
    firstBar: number;
    barCount: number;
    /** Where the right edge sits at offset 0, in bars from `firstBar`. */
    endBar: number;
    /** Where the left edge sits, in bars from `firstBar`. */
    visibleStart: number;
    /** Whether at least a whole bar is hidden that way. */
    canPanEarlier: boolean;
    canPanLater: boolean;
    /** The earlier fade's strength: 0 at the history start, 1 once its width is hidden. */
    earlierFade: number;
}

const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

/**
 * The bars the strip scrolls over: history and `START_GAP_BARS` of the zone
 * before it, so at the earliest position the window's outline and start
 * handle on the history start stay clear of the left edge.
 */
const toTimelineBars = ({
    startDate,
    endDate,
    unit,
}: DensityTimelineParams) => {
    const historyBar = toBarIndex({ date: startDate, unit }) - START_GAP_BARS;
    const endBar = toBarIndex({ date: endDate, unit }) + 1;
    return { historyBar, endBar, historyBars: endBar - historyBar };
};

const toMaxOffset = ({
    historyBars,
    visibleBars,
}: {
    historyBars: number;
    visibleBars: number;
}) => Math.max(historyBars - visibleBars, 0);

/** Whether at least a whole bar is hidden that way at `offset`. */
export const canPanToward = ({
    offset,
    maxOffset,
    direction,
}: {
    offset: number;
    maxOffset: number;
    direction: PanDirection;
}): boolean => (direction === "earlier" ? maxOffset - offset : offset) >= 1;

/**
 * Which bars to draw for a scroll position: the screen in view plus about a
 * screen each side, moving in whole screens so panning rarely redraws. When all
 * history fits, the screen is filled with bars before it.
 */
export function toDensityViewport(
    params: DensityViewportParams,
): DensityViewport {
    const { historyBar, endBar, historyBars } = toTimelineBars(params);
    const visibleBars = Math.max(params.width, 0) / BAR_PITCH_PX;
    const maxOffset = toMaxOffset({ historyBars, visibleBars });
    const offset = clamp(params.offset, 0, maxOffset);
    const page = Math.max(Math.ceil(visibleBars), 1);
    const visibleFrom = endBar - offset - visibleBars;

    let firstBar = endBar - page;
    let lastBarEnd = endBar;
    if (historyBars > visibleBars) {
        const pageIndex = Math.floor((visibleFrom - historyBar) / page);
        firstBar = Math.max(historyBar, historyBar + (pageIndex - 1) * page);
        lastBarEnd = Math.min(endBar, historyBar + (pageIndex + 3) * page);
    }

    return {
        offset,
        maxOffset,
        visibleBars,
        firstBar,
        barCount: lastBarEnd - firstBar,
        endBar: endBar - firstBar,
        visibleStart: visibleFrom - firstBar,
        canPanEarlier: canPanToward({
            offset,
            maxOffset,
            direction: "earlier",
        }),
        canPanLater: canPanToward({ offset, maxOffset, direction: "later" }),
        earlierFade: Math.min(
            ((maxOffset - offset) * BAR_PITCH_PX) / EARLIER_FADE_PX,
            1,
        ),
    };
}

/**
 * Where a pan to `offset` lands: within the limits, and all the way to the
 * edge it's heading for when it would leave less than a bar hidden there,
 * so an arrow never shows for a sliver.
 */
export function panTo({
    viewport: { offset: from, maxOffset },
    offset,
}: {
    viewport: DensityViewport;
    offset: number;
}): number {
    const to = clamp(offset, 0, maxOffset);
    if (to < from && to < 1) {
        return 0;
    }
    if (to > from && maxOffset - to < 1) {
        return maxOffset;
    }
    return to;
}

export interface HeldBars {
    /** The unit they were drawn in. */
    unit: DensityChartBucketUnit;
    firstBar: number;
    barEnd: number;
}

/** Also draws `held`'s bars, so a dragged window stays drawn while the strip pans under it. */
export function withHeldBars(
    viewport: DensityViewport,
    held: HeldBars | null,
): DensityViewport {
    if (!held) {
        return viewport;
    }
    const firstBar = Math.min(viewport.firstBar, held.firstBar);
    const shift = viewport.firstBar - firstBar;
    return {
        ...viewport,
        firstBar,
        barCount:
            Math.max(viewport.firstBar + viewport.barCount, held.barEnd) -
            firstBar,
        endBar: viewport.endBar + shift,
        visibleStart: viewport.visibleStart + shift,
    };
}

export function panOffset({
    viewport,
    direction,
}: {
    viewport: DensityViewport;
    direction: PanDirection;
}): number {
    const step = viewport.visibleBars * PAN_SHARE;
    return panTo({
        viewport,
        offset: viewport.offset + (direction === "earlier" ? step : -step),
    });
}

/**
 * The offset in `to` bars once the bars change unit from `from`: the
 * window's midpoint stays as many px from the right edge, shifted just
 * enough to show all of a window that fits, within the scroll limits. A
 * window ending today keeps today at the right edge.
 */
export function swapOffset({
    window,
    from,
    to,
    ...timeline
}: Omit<DensityTimelineParams, "unit"> & {
    window: DensityChartWindow;
    from: { unit: DensityChartBucketUnit; offset: number };
    to: DensityChartBucketUnit;
}): number {
    if (window.end >= timeline.endDate) {
        return 0;
    }
    const { start } = clampToHistory(window, timeline.startDate);
    const span = (unit: DensityChartBucketUnit) => ({
        start: dayPosition({ date: start, unit }),
        end: dayPosition({ date: addCalendarDays(window.end, 1), unit }),
    });
    const fromSpan = span(from.unit);
    const toSpan = span(to);
    const midToRight =
        toTimelineBars({ ...timeline, unit: from.unit }).endBar -
        from.offset -
        (fromSpan.start + fromSpan.end) / 2;
    const visibleBars = timeline.width / BAR_PITCH_PX;
    let rightEdge = (toSpan.start + toSpan.end) / 2 + midToRight;
    if (toSpan.end - toSpan.start <= visibleBars) {
        rightEdge = clamp(rightEdge, toSpan.end, toSpan.start + visibleBars);
    }
    const { endBar, historyBars } = toTimelineBars({ ...timeline, unit: to });
    return clamp(
        endBar - rightEdge,
        0,
        toMaxOffset({ historyBars, visibleBars }),
    );
}

/**
 * The scroll position that shows a window: where the bars sit at `offset`
 * when it's already in full view there, otherwise at the right edge when it
 * ends today, centered, and with its end in view when it doesn't fit.
 */
export function offsetForWindow(
    params: DensityTimelineParams & {
        window: DensityChartWindow;
        /** Where the bars sit now, if they're placed. */
        offset?: number;
    },
): number {
    const { window, startDate, endDate, unit, width, offset } = params;
    const { endBar, historyBars } = toTimelineBars(params);
    const visibleBars = width / BAR_PITCH_PX;
    const shown = clampToHistory(window, startDate);
    const start = toBarIndex({ date: shown.start, unit });
    const end = toBarIndex({ date: window.end, unit }) + 1;
    if (
        offset !== undefined &&
        start >= endBar - offset - visibleBars &&
        end <= endBar - offset
    ) {
        return offset;
    }
    if (window.end >= endDate) {
        return 0;
    }
    const rightEdge = fitsWindow({ window: shown, unit, visibleBars })
        ? (start + end + visibleBars) / 2
        : end;
    return clamp(
        endBar - rightEdge,
        0,
        toMaxOffset({ historyBars, visibleBars }),
    );
}
